import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Layers, GitCompare, Search, Play, Target, GraduationCap, Zap, Award, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL params to backend game_type values
const GAME_TYPE_MAP = {
  flashcards: 'flashcard',
  matching: 'matching',
  wordsearch: 'word_search'
};

const GAME_INFO = {
  flashcards: { title: 'Flashcards', icon: Layers, color: 'bg-blue-500', textColor: 'text-blue-700', gameType: 'flashcard' },
  matching: { title: 'Matching', icon: GitCompare, color: 'bg-purple-500', textColor: 'text-purple-700', gameType: 'matching' },
  wordsearch: { title: 'Word Search', icon: Search, color: 'bg-emerald-500', textColor: 'text-emerald-700', gameType: 'word_search' }
};

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', icon: GraduationCap },
  { value: 'medium', label: 'Medium', icon: Zap },
  { value: 'hard', label: 'Hard', icon: Award }
];

export default function GameSetup() {
  const { gameType } = useParams();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);  // Multi-select
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);  // Multi-select
  const [availableSubtopics, setAvailableSubtopics] = useState([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);  // Multi-select
  const [questionCount, setQuestionCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const gameInfo = GAME_INFO[gameType] || GAME_INFO.flashcards;
  const GameIcon = gameInfo.icon;
  const backendGameType = GAME_TYPE_MAP[gameType] || 'flashcard';

  const fetchGameTopics = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/games/questions/topics`, { withCredentials: true });
      const allTopics = res.data || [];
      
      // Filter topics that have questions for this specific game type
      const relevantTopics = allTopics.filter(t => t.game_types?.includes(backendGameType));
      
      setTopics(relevantTopics);
    } catch (err) {
      toast.error('Failed to load topics');
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [backendGameType]);

  const checkQuestionCount = useCallback(async () => {
    try {
      const params = {
        game_type: backendGameType,
        topics: selectedTopics.length > 0 ? selectedTopics.join(';;') : undefined,
        difficulties: selectedDifficulties.length > 0 ? selectedDifficulties.join(',') : undefined,
        subtopics: selectedSubtopics.length > 0 ? selectedSubtopics.join(';;') : undefined,
        limit: 1
      };
      const res = await axios.get(`${BACKEND_URL}/api/games/questions`, { params, withCredentials: true });
      setQuestionCount(res.data.meta?.total_available || 0);
    } catch (err) {
      setQuestionCount(0);
    }
  }, [backendGameType, selectedTopics, selectedDifficulties, selectedSubtopics]);

  useEffect(() => {
    fetchGameTopics();
  }, [fetchGameTopics]);

  useEffect(() => {
    // Build subtopic list from selected topics
    if (selectedTopics.length > 0) {
      const allSubtopics = [];
      selectedTopics.forEach(topicName => {
        const topic = topics.find(t => t.topic === topicName);
        if (topic?.subtopics) {
          topic.subtopics.forEach(sub => {
            // Filter out null, undefined, empty strings
            if (sub && typeof sub === 'string' && sub.trim() && !allSubtopics.includes(sub)) {
              allSubtopics.push(sub);
            }
          });
        }
      });
      setAvailableSubtopics(allSubtopics.sort());
    } else {
      setAvailableSubtopics([]);
      setSelectedSubtopics([]);
    }
  }, [selectedTopics, topics]);

  useEffect(() => {
    checkQuestionCount();
  }, [checkQuestionCount]);

  const toggleTopic = (topicName) => {
    setSelectedTopics(prev => {
      if (prev.includes(topicName)) {
        return prev.filter(t => t !== topicName);
      } else {
        return [...prev, topicName];
      }
    });
  };

  const toggleDifficulty = (diff) => {
    setSelectedDifficulties(prev => {
      if (prev.includes(diff)) {
        return prev.filter(d => d !== diff);
      } else {
        return [...prev, diff];
      }
    });
  };

  const toggleSubtopic = (sub) => {
    setSelectedSubtopics(prev => {
      if (prev.includes(sub)) {
        return prev.filter(s => s !== sub);
      } else {
        return [...prev, sub];
      }
    });
  };

  const selectAllTopics = () => {
    if (selectedTopics.length === topics.length) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics(topics.map(t => t.topic));
    }
  };

  const handleStartGame = () => {
    const params = new URLSearchParams();
    if (selectedTopics.length > 0) params.set('topics', selectedTopics.join(';;'));
    if (selectedDifficulties.length > 0) params.set('difficulties', selectedDifficulties.join(','));
    if (selectedSubtopics.length > 0) params.set('subtopics', selectedSubtopics.join(';;'));
    navigate(`/games/${gameType}/play?${params.toString()}`);
  };

  const minQuestions = gameType === 'matching' ? 4 : gameType === 'wordsearch' ? 1 : 1;
  const hasGameQuestions = topics.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // No game questions available yet
  if (!hasGameQuestions) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Button onClick={() => navigate('/games')} variant="ghost" className="hover:bg-slate-100 text-slate-600 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Games
            </Button>
          </div>
        </div>
        
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <div className={`w-16 h-16 ${gameInfo.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <GameIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">{gameInfo.title} Coming Soon</h2>
          <p className="text-slate-600 mb-6">
            No published game questions available yet for {gameInfo.title}. 
            Please check back later or ask an admin to upload game content.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">For Admins</p>
                <p>Upload game questions via Admin → Game Questions → Upload CSV with <code className="bg-amber-100 px-1 rounded">game_type: {backendGameType}</code></p>
                <p className="mt-1">Remember to <strong>publish</strong> questions after reviewing them.</p>
              </div>
            </div>
          </div>
          <Button onClick={() => navigate('/games')} variant="outline" className="mt-6">
            Back to Games
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button onClick={() => navigate('/games')} variant="ghost" className="hover:bg-slate-100 text-slate-600 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Games
          </Button>
          <div className="flex items-center gap-3">
            <div className={`p-2 ${gameInfo.color} rounded-lg`}>
              <GameIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{gameInfo.title} Setup</h1>
              <p className="text-slate-600">Select topics and filters to start playing</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Topic Selection - Multi-select */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Select Topics</h2>
            <button 
              onClick={selectAllTopics}
              className="text-sm text-primary hover:underline"
            >
              {selectedTopics.length === topics.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {topics.map(t => {
              const isSelected = selectedTopics.includes(t.topic);
              return (
                <button 
                  key={t.topic} 
                  onClick={() => toggleTopic(t.topic)} 
                  className={`p-4 rounded-lg border-2 transition-all relative ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <span className="font-medium text-slate-900">{t.topic}</span>
                  <span className="block text-xs text-slate-500 mt-1">{t.question_count} questions</span>
                </button>
              );
            })}
          </div>
          {selectedTopics.length === 0 && (
            <p className="text-sm text-slate-500 mt-2">No topics selected — all topics will be included</p>
          )}
        </div>

        {/* Subtopic Selection - Multi-select (only if topics selected) */}
        {availableSubtopics.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Subtopics (Optional)</h2>
            <div className="flex flex-wrap gap-2">
              {availableSubtopics.map(sub => {
                const isSelected = selectedSubtopics.includes(sub);
                return (
                  <button 
                    key={sub} 
                    onClick={() => toggleSubtopic(sub)} 
                    className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                      isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {sub}
                  </button>
                );
              })}
            </div>
            {selectedSubtopics.length === 0 && (
              <p className="text-sm text-slate-500 mt-2">No subtopics selected — all subtopics will be included</p>
            )}
          </div>
        )}

        {/* Difficulty Selection - Multi-select */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Difficulty (Optional)</h2>
          <div className="grid grid-cols-3 gap-3">
            {DIFFICULTY_OPTIONS.map(d => {
              const Icon = d.icon;
              const isSelected = selectedDifficulties.includes(d.value);
              return (
                <button 
                  key={d.value} 
                  onClick={() => toggleDifficulty(d.value)} 
                  className={`p-4 rounded-lg border-2 transition-all text-center relative ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <Icon className={`w-5 h-5 mx-auto mb-2 ${isSelected ? 'text-primary' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">{d.label}</span>
                </button>
              );
            })}
          </div>
          {selectedDifficulties.length === 0 && (
            <p className="text-sm text-slate-500 mt-2">No difficulty selected — all difficulties will be included</p>
          )}
        </div>

        {/* Start Button */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">
                {questionCount !== null ? `${questionCount} game questions available` : 'Checking...'}
                {questionCount !== null && questionCount < minQuestions && (
                  <span className="text-amber-600 ml-2">(Need at least {minQuestions})</span>
                )}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Filters: {selectedTopics.length || 'All'} topics, {selectedDifficulties.length || 'All'} difficulties
              </p>
            </div>
            <Button 
              onClick={handleStartGame} 
              disabled={questionCount === null || questionCount < minQuestions} 
              className={`h-14 px-8 rounded-full font-semibold text-lg shadow-lg ${gameInfo.color} text-white hover:opacity-90`}
            >
              <Play className="w-5 h-5 mr-2" /> Start {gameInfo.title}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
